/**
 * Formatting for a multi-currency book.
 *
 * Every value that reaches these helpers is a decimal string, never a number.
 * The database stores NUMERIC(19,4); parsing it into a JavaScript float on the
 * way to the screen would reintroduce exactly the error that rule prevents, and
 * a rounding artefact in a trial balance is not a cosmetic bug.
 */

/**
 * An ISO 4217 code. It was `'PKR' | 'USD'`, which made a currency the database
 * accepts unformattable by the screens that display it.
 */
export type Currency = string;
export type Side = 'Dr' | 'Cr';

/**
 * Minor units per currency, for the ones that are not the usual two.
 *
 * A lookup rather than an exhaustive map: JPY has no minor unit and KWD has
 * three, and a `Record<Currency, number>` over an open type cannot be written
 * at all. The register carries `decimals` per currency; this is the fallback
 * for callers that have a code and not the row.
 */
const MINOR_UNITS: Record<string, number> = { JPY: 0, KRW: 0, BHD: 3, KWD: 3, OMR: 3 };
const DEFAULT_MINOR_UNITS = 2;

/** Locale that gives Western digits and comma grouping for both currencies. */
const LOCALE = 'en-PK';

const cache = new Map<string, Intl.NumberFormat>();

function formatter(currency: Currency, decimals: number, showSymbol: boolean): Intl.NumberFormat {
  const key = `${currency}:${decimals}:${showSymbol}`;
  let f = cache.get(key);
  if (!f) {
    try {
      f = new Intl.NumberFormat(LOCALE, {
        style: showSymbol ? 'currency' : 'decimal',
        currency,
        currencyDisplay: 'code',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    } catch {
      // `Intl` throws on a code it does not recognise, and the currency
      // register is a table a person types into — a book could legitimately
      // carry a code Intl has never heard of. Grouping and decimals are what
      // this is really for; the code itself is printed by the column header.
      f = new Intl.NumberFormat(LOCALE, {
        style: 'decimal',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    }
    cache.set(key, f);
  }
  return f;
}

export interface MoneyFormatOptions {
  currency?: Currency;
  /** Defaults to the currency's minor units (2). Pass 0 for summary tiles. */
  decimals?: number;
  /** Prefix with 'PKR' / 'USD'. Off by default: the column header says it once. */
  showCurrency?: boolean;
  /** Render a zero as an em dash, which is how ledgers print an empty cell. */
  dashOnZero?: boolean;
  /** Present negatives as (1,234) rather than -1,234, per accounting convention. */
  parenthesizeNegative?: boolean;
}

/**
 * `formatMoney('12845000.0000')` → `'12,845,000.00'`
 *
 * Rounding happens once, here, using the string's own digits — the value is
 * never widened to a float.
 */
export function formatMoney(
  value: string | null | undefined,
  options: MoneyFormatOptions = {},
): string {
  const {
    currency = 'PKR',
    decimals = MINOR_UNITS[currency] ?? DEFAULT_MINOR_UNITS,
    showCurrency = false,
    dashOnZero = false,
    parenthesizeNegative = true,
  } = options;

  if (value === null || value === undefined || value === '') return dashOnZero ? '—' : '';

  const rounded = roundDecimalString(value, decimals);
  const negative = rounded.startsWith('-');
  const magnitude = negative ? rounded.slice(1) : rounded;

  if (dashOnZero && Number(magnitude) === 0) return '—';

  const text = formatter(currency, decimals, showCurrency).format(Number(magnitude));

  if (!negative) return text;
  return parenthesizeNegative ? `(${text})` : `-${text}`;
}

/** Quantities: 3 dp by default, because a Bag can be 0.5 of a Ton. */
export function formatQty(value: string | null | undefined, decimals = 3): string {
  if (value === null || value === undefined || value === '') return '—';
  const rounded = roundDecimalString(value, decimals);
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(Number(rounded));
}

/** `formatRate('278.40000000')` → `'278.4000'` — FX rates read at 4 dp. */
export function formatRate(value: string | null | undefined, decimals = 4): string {
  if (!value) return '—';
  return roundDecimalString(value, decimals);
}

export function formatPercent(value: string | null | undefined, decimals = 2): string {
  if (value === null || value === undefined || value === '') return '—';
  return `${roundDecimalString(value, decimals)}%`;
}

/**
 * Half-up rounding on the decimal string itself.
 *
 * Intl.NumberFormat would round correctly for values inside Number's safe
 * range, but a PKR balance of 184,206,450.5555 is close enough to the edge of
 * useful precision that doing it on the digits is the honest implementation.
 */
export function roundDecimalString(value: string, decimals: number): string {
  const negative = value.startsWith('-');
  const raw = negative ? value.slice(1) : value;
  const [intPart = '0', fracPart = ''] = raw.split('.');

  if (fracPart.length <= decimals) {
    const padded = fracPart.padEnd(decimals, '0');
    const body = decimals > 0 ? `${intPart}.${padded}` : intPart;
    return negative ? `-${body}` : body;
  }

  const keep = fracPart.slice(0, decimals);
  const nextDigit = fracPart.charCodeAt(decimals) - 48;

  let digits = `${intPart}${keep}`;
  if (nextDigit >= 5) {
    digits = incrementDigits(digits);
  }

  const cut = digits.length - decimals;
  const newInt = (cut > 0 ? digits.slice(0, cut) : '0').replace(/^0+(?=\d)/, '');
  const newFrac = decimals > 0 ? digits.slice(Math.max(cut, 0)).padStart(decimals, '0') : '';

  const body = decimals > 0 ? `${newInt || '0'}.${newFrac}` : newInt || '0';
  return negative && Number(body) !== 0 ? `-${body}` : body;
}

function incrementDigits(digits: string): string {
  const out = digits.split('');
  let i = out.length - 1;
  while (i >= 0) {
    if (out[i] === '9') {
      out[i] = '0';
      i -= 1;
    } else {
      out[i] = String(Number(out[i]) + 1);
      return out.join('');
    }
  }
  return `1${out.join('')}`;
}

/** Which side a signed base amount falls on. */
export function sideOf(signedBase: string): Side {
  return signedBase.startsWith('-') ? 'Cr' : 'Dr';
}

/** `'12845000.0000'` → `'12,845,000.00 Dr'` — how a ledger prints a balance. */
export function formatBalance(signedBase: string, options: MoneyFormatOptions = {}): string {
  const side = sideOf(signedBase);
  const magnitude = signedBase.startsWith('-') ? signedBase.slice(1) : signedBase;
  return `${formatMoney(magnitude, { ...options, parenthesizeNegative: false })} ${side}`;
}

/** Converts a transaction amount to base: base = txn × rate. */
export function toBase(amountTxn: string, fxRate: string, decimals = 4): string {
  const product = Number(amountTxn) * Number(fxRate);
  return roundDecimalString(product.toFixed(8), decimals);
}

// ── Dates ────────────────────────────────────────────────────────────────────
// Stored UTC, rendered Asia/Karachi. Never render a timestamp in the browser's
// zone: a voucher posted at 01:00 PKT would show as the previous day in the UK.

const TZ = 'Asia/Karachi';

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
    .format(d)
    .replace(/\//g, '-');
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
}

export function formatLongDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
}

/** Ageing buckets, matching rpt.v_ar_ageing so the UI and the SQL agree. */
export function ageingBucket(daysOverdue: number): string {
  if (daysOverdue <= 0) return 'Current';
  if (daysOverdue <= 30) return '1-30';
  if (daysOverdue <= 60) return '31-60';
  if (daysOverdue <= 90) return '61-90';
  return '90+';
}
